import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "../api/axios";

const WishlistContext = createContext();

const normalizeItem = (item) => ({
  ...item,
  id: item.id || item._id || item.productId,
  productId: item.productId || item.id || item._id,
});

export const WishlistProvider = ({ children }) => {
  const [wishlist, setWishlist] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const userIdRef = useRef(null);

  // Load wishlist from backend or localStorage
  const loadWishlist = useCallback(async (userId) => {
    setWishlist([]);
    setInitialized(false);

    if (userId) {
      try {
        const res = await api.get("/wishlist");
        if (res.data?.success && Array.isArray(res.data.items)) {
          const normalized = res.data.items.map(normalizeItem);
          setWishlist(normalized);
          localStorage.setItem("wishlist", JSON.stringify(normalized));
          setInitialized(true);
          return;
        }
      } catch {
        // Backend unavailable — show empty rather than stale data
      }
      setWishlist([]);
      localStorage.removeItem("wishlist");
      setInitialized(true);
    } else {
      localStorage.removeItem("wishlist");
      setInitialized(true);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    userIdRef.current = user?._id || null;
    loadWishlist(userIdRef.current);
  }, [loadWishlist]);

  // Re-load when user changes (login/logout in same tab)
  useEffect(() => {
    const checkUser = () => {
      const user = JSON.parse(localStorage.getItem("user"));
      const newId = user?._id || null;
      if (newId !== userIdRef.current) {
        userIdRef.current = newId;
        loadWishlist(newId);
      }
    };

    window.addEventListener("storage", checkUser);
    window.addEventListener("focus", checkUser);
    window.addEventListener("user-login", checkUser);

    return () => {
      window.removeEventListener("storage", checkUser);
      window.removeEventListener("focus", checkUser);
      window.removeEventListener("user-login", checkUser);
    };
  }, [loadWishlist]);

  // Persist to localStorage whenever wishlist changes (after init)
  useEffect(() => {
    if (initialized) {
      localStorage.setItem("wishlist", JSON.stringify(wishlist));
    }
  }, [wishlist, initialized]);

  // Sync to backend (fire-and-forget)
  const syncToBackend = useCallback(async (product) => {
    try {
      await api.post("/wishlist/toggle", {
        productId: product.id || product._id || product.productId,
        title: product.title,
        price: product.price,
        imageUrl: product.imageUrl || product.images?.[0]?.thumbnailUrl || product.images?.[0]?.url || "",
        videoUrl: product.videoUrl || (product.videos?.[0]?.url) || "",
        vendorId: product.vendorId,
      });
    } catch (err) {
      console.error("Wishlist sync failed:", err);
    }
  }, []);

  // Toggle: add or remove
  const toggleWishlist = useCallback((product) => {
    const productId = product.id || product._id || product.productId;
    const imageUrl = product.imageUrl || product.images?.[0]?.thumbnailUrl || product.images?.[0]?.url || "";
    const videoUrl = product.videoUrl || (product.videos?.[0]?.url) || "";

    setWishlist((prev) => {
      const exists = prev.find((item) => (item.id || item._id || item.productId) === productId);
      if (exists) {
        return prev.filter((item) => (item.id || item._id || item.productId) !== productId);
      }
      return [...prev, normalizeItem({
        id: productId, productId,
        title: product.title, price: product.price,
        imageUrl, videoUrl,
        vendorId: product.vendorId,
      })];
    });

    setTimeout(() => syncToBackend({ ...product, imageUrl, videoUrl }), 0);
  }, [syncToBackend]);

  const removeFromWishlist = useCallback((id) => {
    setWishlist((prev) => prev.filter((item) => (item.id || item._id || item.productId) !== id));
    setTimeout(() => syncToBackend({ productId: id }), 0);
  }, [syncToBackend]);

  const clearWishlist = useCallback(async () => {
    setWishlist([]);
    try {
      await api.delete("/wishlist");
    } catch (err) {
      console.error("Clear wishlist sync failed:", err);
    }
  }, []);

  const isWishlisted = useCallback((id) => {
    return wishlist.some((item) => (item.id || item._id || item.productId) === id);
  }, [wishlist]);

  return (
    <WishlistContext.Provider
      value={{
        wishlist,
        toggleWishlist,
        isWishlisted,
        removeFromWishlist,
        clearWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const ctx = useContext(WishlistContext);
  if (!ctx)
    throw new Error("useWishlist must be used within WishlistProvider");
  return ctx;
};